import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ResourceTypeLink } from "@evergis/api";
import { LinearProgress } from "@evergis/ui";

import { DeleteDescription, RequestLoader } from "../styled";
import { CheckboxDescription, TextLine } from "../../DeleteLayer/styled";
import { UsersTooltip } from "../../DeleteLayer/UsersTooltip";
import { ResourcesTooltip } from "./ResourcesTooltip";
import { DeleteContent } from "./DeleteContent";
import { getUsername } from "../../../../ducks/user";
import { getProjectLayers } from "../../../../ducks/project/selectors";
import { userNamesWithAccess } from "./userNamesWithAccess";
import { useDialogInfo } from "../../../../hooks/useDialogs";
import { useAdditionalParameters } from "../hooks/useAdditionalParameters";
import { ResourcesResolved } from "../resolveResources";
import { DeleteResourcesContentProps } from "../types";

type DeleteLayersDescriptionProps = Pick<
  DeleteResourcesContentProps,
  "type" | "toggle" | "checked" | "resourceDependent"
> &
  ResourcesResolved;

export const DeleteLayersDescription: FC<DeleteLayersDescriptionProps> = ({
  type,
  layers,
  projects,
  tables,
  checked,
  toggle,
  resourceDependent = true,
}) => {
  const { t } = useTranslation("dialogs");
  const { resources } = useDialogInfo(resourceDependent);
  const layerInfos = useSelector(getProjectLayers);
  const { showMore, toggleButton } = useAdditionalParameters();

  const currentUser: string = useSelector(getUsername);
  const usersWithLayersAccessNames = useMemo(
    () => userNamesWithAccess(layers, currentUser, t("access.public")),
    [layers, currentUser, t],
  );

  const layersCount = layers?.length;

  const layerAlias = useMemo(
    () => (resources ? layers?.[0]?.alias || layerInfos[resources[0]]?.alias || "" : ""),
    [layerInfos, layers, resources],
  );

  return (
    <div>
      <div>
        {t("deleteResources.confirmDeletion")}{" "}
        {(!layersCount && layerAlias) || layersCount === 1 ? (
          <>
            {t("deleteResources.layerDeletion")} {layerAlias}
          </>
        ) : (
          <ResourcesTooltip resourceType={ResourceTypeLink.Layer} resources={layers} />
        )}
        ?
      </div>
      {toggleButton}
      {showMore && (
        <>
          <DeleteContent
            type={type}
            layers={layers}
            tables={tables}
            projects={projects}
            checked={checked}
            toggle={toggle}
          />
          <DeleteDescription>
            <CheckboxDescription>
              <TextLine>
                <span>
                  {t("deleteResources.usersWithAccess").replace(
                    "{resourceType}",
                    layersCount > 1 ? t("deleteResources.toLayers") : t("deleteResources.toLayer"),
                  )}
                  :&nbsp;
                </span>
                {layersCount ? (
                  <UsersTooltip users={usersWithLayersAccessNames} />
                ) : (
                  <RequestLoader>
                    <LinearProgress />
                  </RequestLoader>
                )}
              </TextLine>
              <TextLine>
                <span>{t("deleteResources.unavailableUsersCatalog")}</span>
              </TextLine>
            </CheckboxDescription>
          </DeleteDescription>
        </>
      )}
    </div>
  );
};
